import { EVENT_LEVEL, HTTP_OPTIONS, TOWER_MENU_ITEMS } from '@silverfort-nodelibs/common/utils/consts';
import { httpGet } from '../components/utils';
import { StatusType } from '@silverfort/sf-components';
import { LayoutData } from './types';
import { getMenuItemIdByMapping } from './pathUtils';
import { UserSession } from '../api';

export const MINUTES_UNTIL_AUTO_LOGOUT = 25;
export const MINUTES_FOR_LOGOUT_ALERT = 2;
export const CHECK_SESSION_INTERVAL = 15000;
export const REFRESH_LAYOUT_DATA_INTERVAL = 60 * 1000;
export const STORE_KEY = 'lastAction';

export const itemIdToUrl = {
    [TOWER_MENU_ITEMS.DASHBOARD]: '/',
    [TOWER_MENU_ITEMS.INVENTORY]: '/Inventory',
    [TOWER_MENU_ITEMS.PROTECTION_POLICIES]: '/Policy',
    [TOWER_MENU_ITEMS.PROTECTION_USER_MANAGEMENT]: '/UserManagement',
    [TOWER_MENU_ITEMS.PA]: '/PrivilegedAccounts',
    [TOWER_MENU_ITEMS.NHI_AD]: '/ServiceAccounts',
    [TOWER_MENU_ITEMS.NHI_CLOUD]: '/CloudNHI',
    [TOWER_MENU_ITEMS.ISPM_AD]: '/Ispm',
    [TOWER_MENU_ITEMS.ISPM_CLOUD]: '/IspmCloud',
    [TOWER_MENU_ITEMS.ITDR_AD]: '/Itdr',
    [TOWER_MENU_ITEMS.ITDR_CLOUD]: '/ItdrCloud',
    [TOWER_MENU_ITEMS.ACCESS_ANALYSIS_AD]: '/AccessAnalysis',
    [TOWER_MENU_ITEMS.ACCESS_ANALYSIS_CLOUD]: '/AccessAnalysisCloud',
    [TOWER_MENU_ITEMS.APP_DISCOVERY]: '/applicationsDiscovery',
    [TOWER_MENU_ITEMS.LOGS]: `/Logs`,
    [TOWER_MENU_ITEMS.MONITOR_NOTIFICATIONS]: '/SystemEvents',
    [TOWER_MENU_ITEMS.MONITOR_SYSTEM_STATUS]: '/SystemStatus',
    [TOWER_MENU_ITEMS.MONITOR_SOFTWARE_UPDATES]: '/SoftwareUpdates',
    [TOWER_MENU_ITEMS.SETTINGS]: '/Settings',
    [TOWER_MENU_ITEMS.LOGOUT]: '/logout',
    [TOWER_MENU_ITEMS.AI_AGENT_SECURITY_CLOUD]: '/AIAgentSecurityCloud',
    [TOWER_MENU_ITEMS.AI_AGENT_PROTECTION_CLOUD]: '/ai-access-control',
    [TOWER_MENU_ITEMS.AUTHENTICATION_FIREWALL_DASHBOARD]: '/AuthenticationFirewallDashboard',
    [TOWER_MENU_ITEMS.UNIVERSAL_MFA_DASHBOARD]: '/UniversalMFADashboard',
    [TOWER_MENU_ITEMS.INCIDENT_INVESTIGATION]: '/Investigation/Incidents'
};

export const getMenuItemIdByPath = (path: string): string | undefined => getMenuItemIdByMapping(path, itemIdToUrl);

export const getLastAction = () => {
    return parseInt(localStorage.getItem(STORE_KEY) || Date.now().toString());
};
export const setLastAction = (time) => {
    localStorage.setItem(STORE_KEY, time.toString());
};

export const stayOnPage = async () => {
    setLastAction(Date.now());
    await httpGet('/data/resetSession', { [HTTP_OPTIONS.HANDLE_ERRORS]: true });
};

export const isUserPermitted = (userData: UserSession | undefined, permissionName: string, permissionLevel: number) => {
    const permissions = userData?.user?.permissions;
    return permissions && permissions[permissionName] >= permissionLevel;
};

export const calcMonitorAlert = (layoutData: LayoutData | undefined, isCloudOnly = false): StatusType | undefined => {
    if (!layoutData) {
        return undefined;
    }

    const systemStatusAlert =
        !isCloudOnly &&
        layoutData &&
        Math.max(layoutData.disconnectedDcsLevel, layoutData.disconnectedPushServicesLevel, layoutData.nodesStatuses);

    const notificationsAlert =
        layoutData?.maxAlarmLevel && layoutData.maxAlarmLevel > EVENT_LEVEL.INFO.value
            ? layoutData.maxAlarmLevel
            : undefined;

    let eventLevel = 0;
    let monitorAlert: StatusType | undefined;

    if (systemStatusAlert) {
        eventLevel = systemStatusAlert;
    }

    if (notificationsAlert) {
        if (systemStatusAlert) {
            eventLevel = Math.max(systemStatusAlert, notificationsAlert);
        } else {
            eventLevel = notificationsAlert;
        }
    }

    if (eventLevel) {
        monitorAlert = eventLevel === EVENT_LEVEL.WARNING.value ? StatusType.Warning : StatusType.Error;
    }

    return monitorAlert;
};

export const calcNotificationsAlert = (layoutData: LayoutData | undefined): StatusType | undefined => {
    if (!layoutData) {
        return undefined;
    }

    const eventLevel =
        layoutData.maxAlarmLevel && layoutData.maxAlarmLevel > EVENT_LEVEL.INFO.value
            ? layoutData.maxAlarmLevel
            : undefined;
    let statusType: StatusType | undefined = undefined;

    if (eventLevel) {
        statusType = eventLevel === EVENT_LEVEL.WARNING.value ? StatusType.Warning : StatusType.Error;
    }

    return statusType;
};

export const calcSystemStatusAlert = (
    layoutData: LayoutData | undefined,
    isCloudOnly = false
): StatusType | undefined => {
    if (!layoutData || isCloudOnly) {
        return undefined;
    }

    let maxAlertLevel = 0;
    let statusType: StatusType | undefined = undefined;

    maxAlertLevel = Math.max(
        layoutData.disconnectedDcsLevel,
        layoutData.disconnectedPushServicesLevel,
        layoutData.nodesStatuses
    );

    if (maxAlertLevel > EVENT_LEVEL.INFO.value) {
        statusType = maxAlertLevel === EVENT_LEVEL.WARNING.value ? StatusType.Warning : StatusType.Error;
    }

    return statusType;
};
